import * as S from './styles';
import { ReactNode, useState, useRef, useEffect } from 'react';
import * as Dialog from 'components/Widget/Dialog';
import Button, { ButtonState, ButtonType } from 'components/Widget/Button/Button';
import { IconType } from 'components/Icon';
import Icon from 'components/Icon';
import { useSelector } from 'react-redux';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';

type ModalProps = {
	title: string;
	cancelLabel?: string | null;
	submitLabel?: string;
	altActionLabel?: string | ReactNode;
	description?: string | ReactNode;
	children?: ReactNode;
	disabled?: boolean;
	modal?: boolean; // if false, no overlay and clicking outside won't close it
	maxWidth?: string; // custom max-width for the modal
	// setting these callbacks causes certain buttons to appear in the modal
	onClose?: () => void; // if set, show an X close button in the top right corner
	onCancel?: () => void; // cancel button on bottom bar
	onSubmit?: () => void; // submit button on bottom bar
	onAltAction?: () => void; // third action button on bottom bar (if undefined but altActionLabel is set, button shows as disabled)
	onClickOutside?: (e: { preventDefault: () => void }) => void;
	onBack?: () => void; // if set, a back button appears in the header that triggers this callback
};

const Modal = ({
	title,
	cancelLabel,
	submitLabel = 'OK',
	altActionLabel,
	description,
	children,
	disabled,
	modal = true,
	maxWidth,
	onClose,
	onCancel,
	onSubmit,
	onAltAction,
	onClickOutside,
	onBack,
}: ModalProps) => {
	const displayCancelLabel = cancelLabel === undefined ? 'Cancel' : cancelLabel;
	const isMobile = useSelector((state: RootReducer) => selectIsMobile(state.mode));
	const [isDragging, setIsDragging] = useState(false);
	const [initialMousePos, setInitialMousePos] = useState({ x: 0, y: 0 });
	const [initialTransform, setInitialTransform] = useState({ x: 0, y: 0 });
	const modalRef = useRef<HTMLDivElement>(null);

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	useEffect(() => {
		if (isDragging) {
			window.addEventListener('mousemove', onMouseMove);
			window.addEventListener('mouseup', onMouseUp);
		} else {
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('mouseup', onMouseUp);
		}

		return () => {
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('mouseup', onMouseUp);
		};
	}, [isDragging]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onMouseDown = (e: React.MouseEvent) => {
		// Disable dragging on mobile
		if (isMobile) return;

		if (e.target instanceof HTMLElement && e.target.closest('.modal-handle')) {
			setIsDragging(true);
			setInitialMousePos({ x: e.clientX, y: e.clientY });

			// store the initial transform of the modal so we can updated it while we drag it
			if (modalRef.current) {
				const rect = modalRef.current.getBoundingClientRect();
				const transform = modalRef.current.style.transform;
				const match = transform.match(/translate\(([-\d.]+)px,\s*([-\d.]+)px\)/);

				if (!match) {
					// The first time this fires, match will be null.
					// We know the modal is centered on the screen, so set its transform appropriately
					setInitialTransform({
						x: (-1 * rect.width) / 2,
						y: (-1 * rect.height) / 2,
					});
				} else {
					// a match indicates we have already been updating the transform using x/ys
					// take these prior values as our initial transform
					setInitialTransform({
						x: parseFloat(match[1]),
						y: parseFloat(match[2]),
					});
				}
			}
		}
	};

	const onMouseMove = (e: MouseEvent) => {
		if (isDragging && modalRef.current) {
			const deltaX = e.clientX - initialMousePos.x;
			const deltaY = e.clientY - initialMousePos.y;

			const newX = initialTransform.x + deltaX;
			const newY = initialTransform.y + deltaY;

			// move the top-left corner of the modal to the newX/Y
			modalRef.current.style.transform = `translate(${newX}px, ${newY}px)`;
		}
	};

	const onMouseUp = () => {
		setIsDragging(false);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<Dialog.Root
			modal={modal}
			open={true}
			onOpenChange={(open: boolean) => {
				if (!open) {
					if (onCancel) {
						onCancel();
					} else if (onClose) {
						onClose();
					}
				}
			}}
		>
			<Dialog.Portal>
				{modal && <Dialog.Overlay />}
				<Dialog.Content
					ref={modalRef}
					className="modal-handle"
					data-mobile={isMobile}
					style={{
						position: 'fixed',
						left: '50%',
						top: '50%',
						// since we don't know the width/height of the modal, we initialize the transform
						// with this 50% business. Later, we will update this using X/Ys when modal is dragged
						transform: 'translate(-50%, -50%)',
						cursor: isDragging ? 'grabbing' : 'auto',
						maxWidth: maxWidth,
					}}
					onPointerDownOutside={(e) => {
						if (modal) {
							if (onClickOutside && !isDragging) onClickOutside(e);
						} else {
							e.preventDefault();
						}
					}}
					onEscapeKeyDown={(e) => {
						e.stopPropagation();
					}}
				>
					{onClose && (
						<S.CloseButton onClick={onClose}>
							<Icon type={IconType.CLOSE} width={16} height={16} />
						</S.CloseButton>
					)}
					<S.TitleRow>
						{onBack && (
							<S.BackButton onClick={onBack}>
								<Icon type={IconType.LINE_ARROW_LEFT} width={16} height={16} />
							</S.BackButton>
						)}
						<Dialog.Title
							style={{
								cursor: isMobile ? 'default' : isDragging ? 'grabbing' : 'grab',
							}}
							onMouseDown={onMouseDown}
						>
							{title}
						</Dialog.Title>
					</S.TitleRow>
					<Dialog.Description>{description}</Dialog.Description>
					{children}
					<S.Buttons $isMobile={isMobile}>
						{onCancel && cancelLabel !== null && cancelLabel !== undefined && (
							<Button onClick={onCancel} type={ButtonType.SECONDARY}>
								{displayCancelLabel}
							</Button>
						)}
						{altActionLabel && (
							<Button
								onClick={onAltAction}
								type={ButtonType.SECONDARY}
								state={onAltAction ? ButtonState.REGULAR : ButtonState.DISABLED}
							>
								{altActionLabel}
							</Button>
						)}
						{onSubmit && (
							<Button
								onClick={onSubmit}
								type={ButtonType.PRIMARY}
								state={disabled ? ButtonState.DISABLED : ButtonState.REGULAR}
							>
								{submitLabel}
							</Button>
						)}
					</S.Buttons>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
};

export default Modal;
